import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { DrawContext, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { RETAINAGE_SECTION_CONFIG } from './sections.config';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { FieldRow } from './FieldRow';
import { useMagicReport } from '../context/MagicReportContext';
import { Box, ToggleButton, ToggleButtonGroup, TextField, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../../common/utils/common.util';
import { orEmpty } from '../../../../common/utils/textFormat.util';
import { calculateTotalCompletedStored } from '../../../project/project.util';
import { LastMonthFieldToggle } from './LastMonthFieldToggle';
import { buildMultiDrawMainComment, DrawRetainage, STANDARD_RETAINAGE_PERCENTAGE } from './retainage-comment.util';

interface RetainageSectionProps {
  magicReportId: string;
}

/**
 * Calculate retainage percentage from line items
 */
const calculateRetainagePercentage = (lineItems?: any[]): number => {
  if (!lineItems || lineItems.length === 0) return 0;

  const filteredLineItems = lineItems.filter((item: any) => !item.isDeleted);

  const totalCompletedAndStored = Number(
    filteredLineItems.reduce((sum: number, li: any) => sum + Number(calculateTotalCompletedStored(li).toFixed(2)), 0).toFixed(2),
  );

  const totalRetainageHeld = -Number(filteredLineItems.reduce((sum: number, li: any) => sum + (li.retainage || 0), 0).toFixed(2));

  if (totalCompletedAndStored === 0) return 0;

  return Number(((Math.abs(totalRetainageHeld) / totalCompletedAndStored) * 100).toFixed(2));
};

/**
 * Calculate completed stored to date for a line item
 */
const calculateCompletedStoredToDate = (lineItem: any): number => {
  return calculateTotalCompletedStored(lineItem);
};

/**
 * Generate auto comments based on retainage value and button states
 */
const generateAutoComments = (
  retainagePercentage: number,
  showList: boolean,
  releaseLanguage: boolean,
  largestReleases: boolean,
  lineItems?: any[],
  drawSummary?: any,
  lastMonthRetainagePercentage?: number,
  /**
   * Retainage per covered G702. When the report covers more than one, the main comment is built
   * from these per-application rates instead of a single blended percentage.
   */
  drawRetainages: DrawRetainage[] = [],
): {
  mainComment: string;
  proceduresList: string;
  releaseProceduresList: string;
  releaseComments: string;
  largestReleasesComment: string;
} => {
  // Format percentage for display
  const formattedPercentage = retainagePercentage.toFixed(2);

  // Calculate difference from last month if available
  // Handle 0 as a valid value - check if it's not undefined/null (0 is a valid number)
  const lastMonthValue =
    lastMonthRetainagePercentage !== undefined && lastMonthRetainagePercentage !== null ? Number(lastMonthRetainagePercentage) : null;
  const retainageDifference = lastMonthValue !== null ? Math.abs(retainagePercentage - lastMonthValue) : null;
  const actualDifference = lastMonthValue !== null ? retainagePercentage - lastMonthValue : null;

  // Procedures list text for Show List (with intro text)
  const proceduresListWithIntro = `Upon Substantial Completion of the construction work we recommend the following procedures in order to process the release of Retainage. Please inform us if the lender has any other procedures they wish to follow:

1- Receipt of either a fully executed Substantial Completion Form, or receipt of a Temporary Certificate of Occupancy noting those items required for final acceptance, or receipt of the permanent, final, or unconditional Certificate of Occupancy. (30 days having passed since the execution of these document(s).)

2- Receipt of the Unconditional lien waiver covering payments through the Contractor's last application for payment including language stating that Contractor has paid all obligations related to the execution of the work through the previous application date.

3- If requested by the bank we will also request an Affidavit of All Bills Paid, an As-Built Survey and a conditional Final Lien Waiver.

Also, prior to the lender's release of Retainage, we recommend the lender's legal counsel review the Loan Documents and make final determination for release of Retainage so that all statutory or other legal rights in favor of the lender are maintained.`;

  // Procedures list text for Release Language (without intro text)
  const proceduresListWithoutIntro = `1- Receipt of either a fully executed Substantial Completion Form, or receipt of a Temporary Certificate of Occupancy noting those items required for final acceptance, or receipt of the permanent, final, or unconditional Certificate of Occupancy. (30 days having passed since the execution of these document(s).)

2- Receipt of the Unconditional lien waiver covering payments through the Contractor's last application for payment including language stating that Contractor has paid all obligations related to the execution of the work through the previous application date.

3- If requested by the bank we will also request an Affidavit of All Bills Paid, an As-Built Survey and a conditional Final Lien Waiver.

Also, prior to the lender's release of Retainage, we recommend the lender's legal counsel review the Loan Documents and make final determination for release of Retainage so that all statutory or other legal rights in favor of the lender are maintained.`;

  // Main comment based on retainage percentage and Release Language state
  let mainComment = '';
  let proceduresList = '';
  let releaseProceduresList = '';
  let releaseCommentsText = '';
  let largestReleasesComment = '';

  // A report covering several G702s judges retainage per application, so the main comment comes
  // from the per-draw rates. Single-draw reports keep the original wording exactly.
  const isMultiDraw = drawRetainages.length > 1;
  const anyDrawBelowStandard = drawRetainages.some(d => d.percentage < STANDARD_RETAINAGE_PERCENTAGE);

  if (releaseLanguage) {
    // Release Language is selected - Show List should be off (mutually exclusive)
    if (isMultiDraw) {
      // The release-language flow appends its own closing sentences, so suppress the statutory one.
      mainComment = buildMultiDrawMainComment(drawRetainages, false);
      if (anyDrawBelowStandard) {
        mainComment += ` The contractor is requesting a partial release of Retainage within the current Application. Prior to our recommendation for the release of any Retainage, it is our policy to require the following:`;
      }
    } else if (retainagePercentage >= 10) {
      // Keep default comment for >= 10%
      mainComment = `Retainage is currently being held at 10% of the Total Completed and Stored to date amount.`;
    } else {
      // Check if retainage changed by < 1% AND is under 10%
      if (retainageDifference !== null && retainageDifference < 1 && retainagePercentage < 10) {
        const formattedDifference = Math.abs(actualDifference!).toFixed(2);
        const changeDirection = actualDifference! < 0 ? 'reduced' : 'increased';
        mainComment = `Note that Retainage is not being held at the standard 10% of the Total Completed and Stored to date amount, but is instead being held at ${formattedPercentage}%. Note that the retainage percentage was ${changeDirection} by ${formattedDifference}% since our last report. We leave to the Lender and their legal counsel the determination of whether the Borrower and Lender are foregoing any of their statutory rights by not holding the full and statutory 10%.`;
      } else {
        // Modified comment for < 10% with release language
        mainComment = `Note that Retainage is not being held at the standard 10% of the Total Completed and Stored to date amount, but is instead being held at ${formattedPercentage}%. The contractor is requesting a partial release of Retainage within the current Application. Prior to our recommendation for the release of any Retainage, it is our policy to require the following:`;
      }
    }
    // Add procedures list (without intro text for Release Language)
    releaseProceduresList = proceduresListWithoutIntro;

    // Add release comments section
    releaseCommentsText = `Since the documents listed above have been provided and 30 days have passed from the date of the completion documents noted above, it is our recommendation to release Retainage, but we leave the final determination of the final release to the discretion of the lender and their counsel.`;
  } else {
    // Show List can be enabled (not mutually exclusive with Release Language when Release Language is off)
    if (isMultiDraw) {
      mainComment = buildMultiDrawMainComment(drawRetainages, true);
    } else if (retainagePercentage >= 10) {
      mainComment = `Retainage is currently being held at 10% of the Total Completed and Stored to date amount.`;
    } else {
      // Check if retainage changed by < 1% AND is under 10%
      if (retainageDifference !== null && retainageDifference < 1 && retainagePercentage < 10) {
        const formattedDifference = Math.abs(actualDifference!).toFixed(2);
        const changeDirection = actualDifference! < 0 ? 'reduced' : 'increased';
        mainComment = `Note that Retainage is not being held at the standard 10% of the Total Completed and Stored to date amount, but is instead being held at ${formattedPercentage}%. Note that the retainage percentage was ${changeDirection} by ${formattedDifference}% since our last report. We leave to the Lender and their legal counsel the determination of whether the Borrower and Lender are foregoing any of their statutory rights by not holding the full and statutory 10%.`;
      } else {
        mainComment = `Note that Retainage is not being held at the standard 10% of the Total Completed and Stored to date amount, but is instead being held at ${formattedPercentage}%. We leave to the Lender and their legal counsel the determination of whether the Borrower and Lender are foregoing any of their statutory rights by not holding the full and statutory 10%.`;
      }
    }

    // Append "Show List" content if enabled (with intro text)
    if (showList) {
      proceduresList = proceduresListWithIntro;
    }
  }

  // Add Largest Releases content if enabled
  if (largestReleases) {
    const filteredLineItems = lineItems?.filter((item: any) => !item.isDeleted) || [];
    const drawName = orEmpty(drawSummary?.name);
    const inputRetainagePercentage = drawSummary?.inputRetainagePercentage || 0;

    // Calculate differences for all line items
    const lineItemsWithDifferences = filteredLineItems
      .map((li: any) => {
        const completedStoredToDate = calculateCompletedStoredToDate(li);
        const calculatedRetainage = (completedStoredToDate * inputRetainagePercentage) / 100;
        const retainageColumnValue = Math.abs(Number(li.retainage || 0));

        // Only calculate difference if retainage column value < calculated retainage
        if (retainageColumnValue < calculatedRetainage) {
          const difference = calculatedRetainage - retainageColumnValue;
          return {
            name: li.lineItem?.description || li.description || 'Unknown',
            difference: difference,
          };
        }
        return null;
      })
      .filter((item: any) => item !== null)
      .sort((a: any, b: any) => b.difference - a.difference);

    // Get top 3 max differences
    const topThreeDifferences = lineItemsWithDifferences.slice(0, 3);
    const topThreeText =
      topThreeDifferences.length > 0
        ? topThreeDifferences
            .map((li: any) => {
              const formattedAmount = formatCurrency(li.difference.toString(), false);
              return `${li.name} ($${formattedAmount})`;
            })
            .join(', ')
        : 'None';

    const onTheApplicationClause = drawName ? `On the ${drawName} application the` : 'The';
    largestReleasesComment = `${onTheApplicationClause} following are notable retainage releases: The largest releases to date are on the following line items: ${topThreeText}. The largest current retainage releases are on the following line items: None. Retainage is first being released on some line items and the largest of these is as follows: None. The largest line items to be completely zeroed out this period are the following: None.`;
  }

  return {
    mainComment,
    proceduresList,
    releaseProceduresList,
    releaseComments: releaseCommentsText,
    largestReleasesComment,
  };
};

const RetainageSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const { reportState, registerSectionSync, unregisterSectionSync } = useMagicReport();
  const commentsField = getCommentsField(data, RETAINAGE_SECTION_CONFIG.config);

  // Get button states directly from data to avoid stale values
  const showListField = data.fields.find(f => f.fieldName === 'ShowList');
  const releaseLanguageField = data.fields.find(f => f.fieldName === 'ReleaseRetainageLanguage');
  const largestReleasesField = data.fields.find(f => f.fieldName === 'LargestReleases');

  const showList = showListField?.value === 'true';
  const releaseLanguage = releaseLanguageField?.value === 'true';
  const largestReleases = largestReleasesField?.value === 'true';

  // Every covered draw (G702). Reports opened before multi-draw have no list, so the single
  // primary-draw mirror becomes a one-draw list and every comment renders exactly as before.
  const draws: DrawContext[] = useMemo(() => {
    const fromState = reportState.draws as DrawContext[] | undefined;
    if (fromState?.length) return fromState;
    if (!reportState.drawSummary) return [];
    return [
      {
        drawId: reportState.drawSummary.id,
        drawSummary: reportState.drawSummary,
        budgetSummary: reportState.budgetSummary,
        lineItems: reportState.lineItems,
      },
    ];
  }, [reportState.draws, reportState.drawSummary, reportState.budgetSummary, reportState.lineItems]);

  // Retainage is calculated on each payapp independently — the comment reports per application.
  const drawRetainages: DrawRetainage[] = useMemo(
    () =>
      draws.map(draw => ({
        drawId: draw.drawId,
        name: draw.drawSummary?.name || '[G702 Name]',
        percentage: calculateRetainagePercentage(draw.lineItems),
      })),
    [draws],
  );

  // Signature so the comment regenerates when any single draw's retainage moves, not just the blend.
  const drawRetainagesKey = useMemo(() => JSON.stringify(drawRetainages), [drawRetainages]);

  // Blended percentage across every covered draw. Still stored in RetainagePercentage and used for
  // the single-draw wording and the month-over-month comparison.
  const retainagePercentage = useMemo(() => {
    return calculateRetainagePercentage(draws.flatMap(d => d.lineItems || []));
  }, [draws]);

  // Get last month retainage percentage
  const lastMonthRetainagePercentage = useMemo(() => {
    const retainagePercentageField = data.fields.find(f => f.fieldName === 'RetainagePercentage');
    const lastMonthValue = retainagePercentageField?.lastMonth;
    // Handle "0" as a valid value - check if it's not null/undefined and not empty string
    if (lastMonthValue !== null && lastMonthValue !== undefined && lastMonthValue.trim() !== '') {
      const numValue = Number(lastMonthValue);
      // Return the number value, even if it's 0
      return !isNaN(numValue) ? numValue : undefined;
    }
    return undefined;
  }, [data.fields]);

  // Store calculated retainage percentage in the RetainagePercentage field
  useEffect(() => {
    const retainagePercentageField = getField('RetainagePercentage');
    const calculatedValue = retainagePercentage.toFixed(2);

    // Only update if the value has changed to avoid infinite loops
    if (retainagePercentageField?.value !== calculatedValue) {
      handleFieldChange('RetainagePercentage', { value: calculatedValue });
    }
  }, [retainagePercentage, getField, handleFieldChange]);

  // Track previous button states to detect changes
  const prevButtonStatesRef = useRef<{
    showList: boolean;
    releaseLanguage: boolean;
    largestReleases: boolean;
    retainagePercentage: number;
    lastMonthRetainagePercentage: number | undefined;
    drawRetainagesKey: string;
  }>({
    showList: showList,
    releaseLanguage: releaseLanguage,
    largestReleases: largestReleases,
    retainagePercentage: retainagePercentage,
    lastMonthRetainagePercentage: lastMonthRetainagePercentage,
    drawRetainagesKey: drawRetainagesKey,
  });
  const hasPopulatedInitialCommentRef = useRef(false);

  // Auto-generate text ONLY when button states change or retainage changes
  useEffect(() => {
    const prevStates = prevButtonStatesRef.current;
    const currentShowList = showListField?.value === 'true';
    const currentReleaseLanguage = releaseLanguageField?.value === 'true';
    const currentLargestReleases = largestReleasesField?.value === 'true';

    // Check if MainComment has saved data - if so, mark as populated to prevent overwriting
    const mainCommentField = getField('MainComment');
    const isMainCommentEmpty = !mainCommentField?.value || mainCommentField.value.trim() === '';

    // If MainComment has a value, it's saved data - mark as populated immediately to prevent overwriting
    if (!hasPopulatedInitialCommentRef.current && !isMainCommentEmpty) {
      hasPopulatedInitialCommentRef.current = true;
    }

    // Check if button states or retainage changed
    const showListChanged = prevStates.showList !== currentShowList;
    const releaseLanguageChanged = prevStates.releaseLanguage !== currentReleaseLanguage;
    const largestReleasesChanged = prevStates.largestReleases !== currentLargestReleases;
    const retainageChanged = prevStates.retainagePercentage !== retainagePercentage;
    const lastMonthChanged = prevStates.lastMonthRetainagePercentage !== lastMonthRetainagePercentage;
    // A per-draw rate can move without shifting the blended figure, so track it separately.
    const drawRetainagesChanged = prevStates.drawRetainagesKey !== drawRetainagesKey;

    // Only populate initial comment once, and only if the field is empty (meaning it's a new report, not saved data)
    const shouldPopulateInitial = !hasPopulatedInitialCommentRef.current && isMainCommentEmpty;

    // Generate comment if something changed OR if we need to populate initial comment
    const shouldGenerate =
      showListChanged ||
      releaseLanguageChanged ||
      largestReleasesChanged ||
      retainageChanged ||
      drawRetainagesChanged ||
      lastMonthChanged ||
      shouldPopulateInitial;

    if (shouldGenerate) {
      const { mainComment, proceduresList, releaseProceduresList, releaseComments, largestReleasesComment } = generateAutoComments(
        retainagePercentage,
        currentShowList,
        currentReleaseLanguage,
        currentLargestReleases,
        reportState.lineItems,
        reportState.drawSummary,
        lastMonthRetainagePercentage,
        drawRetainages,
      );

      // Update MainComment - only regenerate when button states change, or populate initial if empty
      // Don't regenerate if MainComment already has saved data (preserve user edits)
      if (showListChanged || releaseLanguageChanged) {
        // Regenerate MainComment when button states change (user action)
        handleFieldChange('MainComment', { value: mainComment });
      } else if (shouldPopulateInitial) {
        // Only populate on initial mount if MainComment is empty (new report, not saved data)
        handleFieldChange('MainComment', { value: mainComment });
        hasPopulatedInitialCommentRef.current = true;
      } else if ((retainageChanged || drawRetainagesChanged || lastMonthChanged) && isMainCommentEmpty) {
        // Only update if retainage or lastMonth changed AND MainComment is empty (new report, not saved data)
        handleFieldChange('MainComment', { value: mainComment });
      }

      // Update ProceduresList - only when Show List is turned ON
      if (showListChanged && currentShowList) {
        const proceduresListField = getField('ProceduresList');
        if (proceduresListField) {
          const isEmpty = !proceduresListField.value || proceduresListField.value.trim() === '';
          if (isEmpty) {
            handleFieldChange('ProceduresList', { value: proceduresList });
          }
        }
      } else if (showListChanged && !currentShowList) {
        // Clear when Show List is turned OFF
        handleFieldChange('ProceduresList', { value: '' });
      }

      // Update ReleaseProceduresList - only when Release Language is turned ON
      if (releaseLanguageChanged && currentReleaseLanguage) {
        const releaseProceduresListField = getField('ReleaseProceduresList');
        if (releaseProceduresListField) {
          const isEmpty = !releaseProceduresListField.value || releaseProceduresListField.value.trim() === '';
          if (isEmpty) {
            handleFieldChange('ReleaseProceduresList', { value: releaseProceduresList });
          }
        }
      } else if (releaseLanguageChanged && !currentReleaseLanguage) {
        // Clear when Release Language is turned OFF
        handleFieldChange('ReleaseProceduresList', { value: '' });
      }

      // Update ReleaseComments - only when Release Language is turned ON
      if (releaseLanguageChanged && currentReleaseLanguage) {
        const releaseCommentsField = getField('ReleaseComments');
        if (releaseCommentsField) {
          const isEmpty = !releaseCommentsField.value || releaseCommentsField.value.trim() === '';
          if (isEmpty) {
            handleFieldChange('ReleaseComments', { value: releaseComments });
          }
        }
      } else if (releaseLanguageChanged && !currentReleaseLanguage) {
        // Clear when Release Language is turned OFF
        handleFieldChange('ReleaseComments', { value: '' });
      }

      // Update LargestReleasesComment - only when Largest Releases is turned ON
      if (largestReleasesChanged && currentLargestReleases) {
        const largestReleasesField = getField('LargestReleasesComment');
        if (largestReleasesField) {
          const isEmpty = !largestReleasesField.value || largestReleasesField.value.trim() === '';
          if (isEmpty) {
            handleFieldChange('LargestReleasesComment', { value: largestReleasesComment });
          }
        }
      } else if (largestReleasesChanged && !currentLargestReleases) {
        // Clear when Largest Releases is turned OFF
        handleFieldChange('LargestReleasesComment', { value: '' });
      }

      // Update ref with current states
      prevButtonStatesRef.current = {
        showList: currentShowList,
        releaseLanguage: currentReleaseLanguage,
        largestReleases: currentLargestReleases,
        retainagePercentage: retainagePercentage,
        lastMonthRetainagePercentage: lastMonthRetainagePercentage,
        drawRetainagesKey: drawRetainagesKey,
      };
    }
  }, [
    retainagePercentage,
    drawRetainagesKey,
    drawRetainages,
    lastMonthRetainagePercentage,
    showListField?.value,
    releaseLanguageField?.value,
    largestReleasesField?.value,
    reportState.lineItems,
    reportState.drawSummary,
    getField,
    handleFieldChange,
  ]);

  // Register sync function to be called when "Run this Month" is clicked
  useEffect(() => {
    const syncFn = async () => {
      // Get current button states
      const currentShowList = showListField?.value === 'true';
      const currentReleaseLanguage = releaseLanguageField?.value === 'true';
      const currentLargestReleases = largestReleasesField?.value === 'true';

      // Calculate the current blended retainage percentage across every covered draw
      const currentRetainagePercentage = calculateRetainagePercentage(draws.flatMap(d => d.lineItems || []));

      // Get last month value
      const retainagePercentageField = getField('RetainagePercentage');
      const lastMonthValue = retainagePercentageField?.lastMonth;
      const lastMonthPercentage = lastMonthValue && lastMonthValue.trim() !== '' ? Number(lastMonthValue) : undefined;

      // Generate auto comments based on current state
      const { mainComment, proceduresList, releaseProceduresList, releaseComments, largestReleasesComment } = generateAutoComments(
        currentRetainagePercentage,
        currentShowList,
        currentReleaseLanguage,
        currentLargestReleases,
        reportState.lineItems,
        reportState.drawSummary,
        lastMonthPercentage,
        drawRetainages,
      );

      // Force update all comment fields to reset them according to current budget
      handleFieldChange('MainComment', { value: mainComment });

      // Update ProceduresList based on Show List state
      if (currentShowList) {
        handleFieldChange('ProceduresList', { value: proceduresList });
      } else {
        handleFieldChange('ProceduresList', { value: '' });
      }

      // Update ReleaseProceduresList based on Release Language state
      if (currentReleaseLanguage) {
        handleFieldChange('ReleaseProceduresList', { value: releaseProceduresList });
      } else {
        handleFieldChange('ReleaseProceduresList', { value: '' });
      }

      // Update ReleaseComments based on Release Language state
      if (currentReleaseLanguage) {
        handleFieldChange('ReleaseComments', { value: releaseComments });
      } else {
        handleFieldChange('ReleaseComments', { value: '' });
      }

      // Update LargestReleasesComment based on Largest Releases state
      if (currentLargestReleases) {
        handleFieldChange('LargestReleasesComment', { value: largestReleasesComment });
      } else {
        handleFieldChange('LargestReleasesComment', { value: '' });
      }
    };

    registerSectionSync('retainage', syncFn);
    return () => {
      unregisterSectionSync('retainage');
    };
  }, [
    registerSectionSync,
    unregisterSectionSync,
    showListField?.value,
    releaseLanguageField?.value,
    largestReleasesField?.value,
    draws,
    drawRetainages,
    reportState.lineItems,
    reportState.drawSummary,
    getField,
    handleFieldChange,
  ]);

  // Restore a single field back to its auto-generated text, using the current toggle/retainage state.
  const handleRefreshField = useCallback(
    (fieldName: 'MainComment' | 'ProceduresList' | 'ReleaseProceduresList' | 'ReleaseComments' | 'LargestReleasesComment') => {
      const auto = generateAutoComments(
        retainagePercentage,
        showList,
        releaseLanguage,
        largestReleases,
        reportState.lineItems,
        reportState.drawSummary,
        lastMonthRetainagePercentage,
        drawRetainages,
      );

      const autoValueByField: Record<string, string> = {
        MainComment: auto.mainComment,
        ProceduresList: auto.proceduresList,
        ReleaseProceduresList: auto.releaseProceduresList,
        ReleaseComments: auto.releaseComments,
        LargestReleasesComment: auto.largestReleasesComment,
      };

      handleFieldChange(fieldName, { value: autoValueByField[fieldName] || '' });
    },
    [
      retainagePercentage,
      showList,
      releaseLanguage,
      largestReleases,
      reportState.lineItems,
      reportState.drawSummary,
      lastMonthRetainagePercentage,
      drawRetainages,
      handleFieldChange,
    ],
  );

  const renderRefreshAction = useCallback(
    (fieldName: 'MainComment' | 'ProceduresList' | 'ReleaseProceduresList' | 'ReleaseComments' | 'LargestReleasesComment') => (
      <Box
        component='span'
        onClick={() => handleRefreshField(fieldName)}
        title='Restore auto-comment'
        sx={{ cursor: 'pointer', color: 'primary.main', display: 'flex' }}
      >
        <RefreshIcon fontSize='small' />
      </Box>
    ),
    [handleRefreshField],
  );

  // Get current button values for ToggleButtonGroup
  const currentButtonValues = useMemo(() => {
    const values: string[] = [];
    if (showList) values.push('ShowList');
    if (releaseLanguage) values.push('ReleaseRetainageLanguage');
    if (largestReleases) values.push('LargestReleases');
    return values;
  }, [showList, releaseLanguage, largestReleases]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ width: 21, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <ToggleButtonGroup
                value={currentButtonValues}
                onChange={(_, newValues: string[]) => {
                  // Handle multiple selections - newValues is an array of selected button values
                  const selectedValues = Array.isArray(newValues) ? newValues : [];

                  // Read current values directly from fields (always fresh, no stale state)
                  const currentShowList = showListField?.value === 'true';
                  const currentReleaseLanguage = releaseLanguageField?.value === 'true';

                  // Determine what user wants
                  const wantsShowList = selectedValues.includes('ShowList');
                  const wantsReleaseLanguage = selectedValues.includes('ReleaseRetainageLanguage');
                  const wantsLargestReleases = selectedValues.includes('LargestReleases');

                  // Show List and Release Retainage Language are mutually exclusive
                  // Simple rule: if selecting one while other is on, turn off the other and turn on the selected one
                  if (wantsShowList && currentReleaseLanguage) {
                    // User wants Show List but Release Language is on - switch them
                    handleFieldChange('ReleaseRetainageLanguage', { value: 'false' });
                    handleFieldChange('ShowList', { value: 'true' });
                  } else if (wantsReleaseLanguage && currentShowList) {
                    // User wants Release Language but Show List is on - switch them
                    handleFieldChange('ShowList', { value: 'false' });
                    handleFieldChange('ReleaseRetainageLanguage', { value: 'true' });
                  } else {
                    // No conflict - just update what changed
                    if (wantsShowList !== currentShowList) {
                      handleFieldChange('ShowList', { value: wantsShowList ? 'true' : 'false' });
                    }
                    if (wantsReleaseLanguage !== currentReleaseLanguage) {
                      handleFieldChange('ReleaseRetainageLanguage', { value: wantsReleaseLanguage ? 'true' : 'false' });
                    }
                  }

                  // Largest Releases can be toggled independently
                  if (wantsLargestReleases !== largestReleases) {
                    handleFieldChange('LargestReleases', { value: wantsLargestReleases ? 'true' : 'false' });
                  }
                }}
                sx={{
                  width: '100%',
                  '& .MuiToggleButton-root': {
                    flex: 1,
                    py: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      borderColor: 'primary.main',
                      position: 'relative',
                      // Add visible left border to separate from previous selected button
                      '& + .Mui-selected': {
                        borderLeft: '1px solid rgba(255, 255, 255, 1) !important',
                      },
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover:not(.Mui-selected)': {
                      backgroundColor: 'action.hover',
                    },
                    // Remove double borders between buttons
                    '&:not(:first-of-type)': {
                      marginLeft: '-1px',
                    },
                  },
                }}
              >
                <ToggleButton value='ShowList'>Show List</ToggleButton>
                <ToggleButton value='ReleaseRetainageLanguage'>Release Retainage Language</ToggleButton>
                <ToggleButton value='LargestReleases'>Largest Releases</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          <Box sx={{ width: 30, flexShrink: 0 }} />
        </Box>
      </Box>

      {/* Text fields for auto-generated content - only show when relevant */}
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* MainComment - always show */}
        {getField('MainComment') && (
          <Box>
            <Box sx={{ mb: 0.5 }}>
              <LastMonthFieldToggle
                lastMonthValue={getField('MainComment')?.lastMonth}
                label='Retainage Comment'
                reserveTrailingActionSpace
              />
            </Box>
            <FieldRow
              addToActionItems={getField('MainComment')?.addToActionItems}
              onBookmarkToggle={() => {
                const field = getField('MainComment');
                if (field) handleFieldChange('MainComment', { addToActionItems: !field.addToActionItems });
              }}
              alignStart
              trailingAction={renderRefreshAction('MainComment')}
            >
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={20}
                value={getField('MainComment')?.value || ''}
                onChange={e => handleFieldChange('MainComment', { value: e.target.value })}
              />
            </FieldRow>
          </Box>
        )}

        {/* ProceduresList - only show when Show List is enabled */}
        {showList && getField('ProceduresList') && (
          <Box>
            <Box sx={{ mb: 0.5 }}>
              <LastMonthFieldToggle
                lastMonthValue={getField('ProceduresList')?.lastMonth}
                label='Procedures List'
                reserveTrailingActionSpace
              />
            </Box>
            <FieldRow
              addToActionItems={getField('ProceduresList')?.addToActionItems}
              onBookmarkToggle={() => {
                const field = getField('ProceduresList');
                if (field) handleFieldChange('ProceduresList', { addToActionItems: !field.addToActionItems });
              }}
              alignStart
              trailingAction={renderRefreshAction('ProceduresList')}
            >
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={20}
                value={getField('ProceduresList')?.value || ''}
                onChange={e => handleFieldChange('ProceduresList', { value: e.target.value })}
              />
            </FieldRow>
          </Box>
        )}

        {/* ReleaseProceduresList - only show when Release Language is enabled */}
        {releaseLanguage && getField('ReleaseProceduresList') && (
          <Box>
            <Box sx={{ mb: 0.5 }}>
              <LastMonthFieldToggle
                lastMonthValue={getField('ReleaseProceduresList')?.lastMonth}
                label='Release Procedures List'
                reserveTrailingActionSpace
              />
            </Box>
            <FieldRow
              addToActionItems={getField('ReleaseProceduresList')?.addToActionItems}
              onBookmarkToggle={() => {
                const field = getField('ReleaseProceduresList');
                if (field) handleFieldChange('ReleaseProceduresList', { addToActionItems: !field.addToActionItems });
              }}
              alignStart
              trailingAction={renderRefreshAction('ReleaseProceduresList')}
            >
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={20}
                value={getField('ReleaseProceduresList')?.value || ''}
                onChange={e => handleFieldChange('ReleaseProceduresList', { value: e.target.value })}
              />
            </FieldRow>
          </Box>
        )}

        {/* ReleaseComments - only show when Release Language is enabled, with heading */}
        {releaseLanguage && getField('ReleaseComments') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2' color='text' fontWeight='medium'>
                Release Comments
              </Typography>
              <LastMonthFieldToggle
                lastMonthValue={getField('ReleaseComments')?.lastMonth}
                label='Release Comments'
                reserveTrailingActionSpace
              />
            </Box>
            <FieldRow
              addToActionItems={getField('ReleaseComments')?.addToActionItems}
              onBookmarkToggle={() => {
                const field = getField('ReleaseComments');
                if (field) handleFieldChange('ReleaseComments', { addToActionItems: !field.addToActionItems });
              }}
              alignStart
              trailingAction={renderRefreshAction('ReleaseComments')}
            >
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={20}
                value={getField('ReleaseComments')?.value || ''}
                onChange={e => handleFieldChange('ReleaseComments', { value: e.target.value })}
              />
            </FieldRow>
          </Box>
        )}

        {/* LargestReleasesComment - only show when Largest Releases is enabled, with label */}
        {largestReleases && getField('LargestReleasesComment') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2' color='text' fontWeight='medium'>
                Largest Releases
              </Typography>
              <LastMonthFieldToggle
                lastMonthValue={getField('LargestReleasesComment')?.lastMonth}
                label='Largest Releases'
                reserveTrailingActionSpace
              />
            </Box>
            <FieldRow
              addToActionItems={getField('LargestReleasesComment')?.addToActionItems}
              onBookmarkToggle={() => {
                const field = getField('LargestReleasesComment');
                if (field) handleFieldChange('LargestReleasesComment', { addToActionItems: !field.addToActionItems });
              }}
              alignStart
              trailingAction={renderRefreshAction('LargestReleasesComment')}
            >
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={20}
                value={getField('LargestReleasesComment')?.value || ''}
                onChange={e => handleFieldChange('LargestReleasesComment', { value: e.target.value })}
              />
            </FieldRow>
          </Box>
        )}
      </Box>

      {/* Comments Section */}
      {commentsField && (
        <Box sx={{ mt: 3 }}>
          <CommentsSection
            commentsField={commentsField}
            onChange={updated => handleFieldChange('Comments', updated)}
            title='Comments'
            onBook={onBook}
          />
        </Box>
      )}
    </>
  );
};

export const RetainageSection: React.FC<RetainageSectionProps> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={RETAINAGE_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <RetainageSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
